import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DiveSelector } from "@/components/DiveSelector";
import { DiveChart } from "@/components/DiveChart";
import { DiveStats } from "@/components/DiveStats";
import { MediaTimeline } from "@/components/MediaTimeline";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import LightRays from "@/components/LightRays";
import Bubbles from "@/components/Bubbles";
import DiveMap from "@/components/DiveMap";
import type { DiveListItem, DiveDetail } from "@/lib/database";
import type { DiveMetadata, MediaItem } from "@/lib/metadata";
import "./index.css";

interface DiveWithMetadata extends DiveDetail {
  metadata: DiveMetadata | null;
}

interface DiveListItemWithMetadata extends DiveListItem {
  metadata: DiveMetadata | null;
}

export function App() {
  const [dives, setDives] = useState<DiveListItemWithMetadata[]>([]);
  const [selectedDiveId, setSelectedDiveId] = useState<string | null>(null);
  const [selectedDive, setSelectedDive] = useState<DiveWithMetadata | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<MediaItem | null>(null);

  // Navigation helpers (array is newest-first, so prev = higher index, next = lower index)
  const currentIndex = dives.findIndex((d) => d.id === selectedDiveId);
  const prevDive = currentIndex < dives.length - 1 ? dives[currentIndex + 1] : null;
  const nextDive = currentIndex > 0 ? dives[currentIndex - 1] : null;

  const goToPrev = () => {
    if (prevDive) {
      window.scrollTo({ top: 0, behavior: "instant" });
      setSelectedDiveId(prevDive.id);
    }
  };
  const goToNext = () => {
    if (nextDive) {
      window.scrollTo({ top: 0, behavior: "instant" });
      setSelectedDiveId(nextDive.id);
    }
  };

  // Keyboard navigation (disabled when lightbox is open)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedPhoto) return; // Let lightbox handle navigation
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [prevDive, nextDive, selectedPhoto]);

  // Fetch dive list on mount
  useEffect(() => {
    fetch("/api/dives")
      .then((res) => res.json())
      .then((data) => {
        setDives(data);
        // Auto-select Dive #1 (lowest dive number)
        if (data.length > 0) {
          const firstDive = data.reduce((min: DiveListItemWithMetadata, d: DiveListItemWithMetadata) =>
            d.diveNumber < min.diveNumber ? d : min
          , data[0]);
          setSelectedDiveId(firstDive.id);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load dives");
        setLoading(false);
        console.error(err);
      });
  }, []);

  // Fetch selected dive details
  useEffect(() => {
    if (!selectedDiveId) {
      setSelectedDive(null);
      return;
    }

    setLoading(true);
    fetch(`/api/dives/${selectedDiveId}`)
      .then((res) => res.json())
      .then((data) => {
        setSelectedDive(data);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load dive details");
        setLoading(false);
        console.error(err);
      });
  }, [selectedDiveId]);

  if (error) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <Card className="border-destructive">
          <CardContent className="p-6 text-center text-destructive">
            {error}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* Underwater light rays background */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d2847] via-[#0a1e3a] via-60% to-[#03101a]">
        <LightRays
          raysOrigin="top-center"
          raysColor="#4fc3f7"
          raysSpeed={0.5}
          lightSpread={1.2}
          rayLength={1.5}
          followMouse={false}
          mouseInfluence={0}
          noiseAmount={0.05}
          distortion={0.02}
          saturation={0.8}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-2 pt-4 pb-8 sm:px-4 sm:pt-8 sm:pb-12 max-w-4xl">
        <header className="mb-4 sm:mb-6">
          <h1 className="text-3xl sm:text-4xl font-light mb-6 sm:mb-8 mt-2 sm:mt-4 text-white text-center">
            Bain's Dive Log
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrev}
              disabled={!prevDive}
              className="h-9 px-3 text-sm rounded-xl border border-white/10 bg-[#081a32]/85 backdrop-blur-sm shadow-lg cursor-pointer hover:bg-[#0a2040] hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
              aria-label="Previous dive"
            >
              &larr;
            </button>
            <DiveSelector
              dives={dives}
              selectedId={selectedDiveId}
              onSelect={setSelectedDiveId}
            />
            <button
              onClick={goToNext}
              disabled={!nextDive}
              className="h-9 px-3 text-sm rounded-xl border border-white/10 bg-[#081a32]/85 backdrop-blur-sm shadow-lg cursor-pointer hover:bg-[#0a2040] hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
              aria-label="Next dive"
            >
              &rarr;
            </button>
          </div>
        </header>

        {loading && !selectedDive && (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Loading...
            </CardContent>
          </Card>
        )}

        {selectedDive && (
          <main className="space-y-4 sm:space-y-6">
            {/* Header Image */}
            {selectedDive.metadata?.headerImage && (
              <div className="rounded-xl overflow-hidden">
                {selectedDive.metadata.headerImage.endsWith('.mp4') || selectedDive.metadata.headerImage.endsWith('.mov') ? (
                  <video
                    src={selectedDive.metadata.headerImage}
                    className="w-full h-56 sm:h-80 object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={selectedDive.metadata.headerImage}
                    alt={selectedDive.metadata.customName || `Dive #${selectedDive.diveNumber}`}
                    className="w-full h-56 sm:h-80 object-cover"
                  />
                )}
              </div>
            )}

            {/* Dive Info Header */}
            <Card>
              <CardHeader className="pb-0 space-y-0.5">
                <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-lg sm:text-xl">
                    {selectedDive.metadata?.customName || `Dive #${selectedDive.diveNumber}`}
                  </span>
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {new Date(selectedDive.date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  {" "}&bull;{" "}
                  {selectedDive.location}
                </p>
              </CardHeader>
              {selectedDive.metadata?.summary && (
                <CardContent className="pt-1">
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">
                    {selectedDive.metadata.summary}
                  </p>
                </CardContent>
              )}
            </Card>

            {/* Map */}
            {selectedDive.metadata?.coordinates && (
              <DiveMap
                coordinates={selectedDive.metadata.coordinates}
                className="h-[300px] sm:h-[400px]"
              />
            )}

            {/* Stats */}
            <DiveStats dive={selectedDive} />

            {/* Chart */}
            <Card className="mt-4">
              <CardHeader className="space-y-3">
                <CardTitle className="text-lg">Dive Profile</CardTitle>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <p>
                    <span className="text-[#3b82f6] font-medium">Depth</span> —
                    Distance below the surface
                  </p>
                  <p>
                    <span className="text-[#fdba74] font-medium">
                      Temperature
                    </span>{" "}
                    — Water temperature
                  </p>
                  <p>
                    <span className="text-[#f87171] font-medium">NDL</span> — No
                    Decompression Limit (safe bottom time remaining)
                  </p>
                </div>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                {selectedDive.samples.length > 0 ? (
                  <DiveChart
                    samples={selectedDive.samples}
                    showTemperature={true}
                    showNdl={true}
                  />
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No sample data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Media Timeline */}
            {selectedDive.metadata?.media && selectedDive.metadata.media.length > 0 && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Photos</CardTitle>
                </CardHeader>
                <CardContent>
                  <MediaTimeline
                    media={selectedDive.metadata.media}
                    diveDuration={selectedDive.durationSeconds}
                    samples={selectedDive.samples}
                    onPhotoClick={(photo) => setSelectedPhoto(photo)}
                  />
                </CardContent>
              </Card>
            )}

            {/* Bottom Navigation */}
            <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-white/10">
              <button
                onClick={goToPrev}
                disabled={!prevDive}
                className="flex-1 text-left p-3 rounded-xl border border-white/10 bg-[#081a32]/85 backdrop-blur-sm shadow-lg cursor-pointer hover:bg-[#0a2040] hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                aria-label="Previous dive"
              >
                {prevDive ? (
                  <>
                    <span className="text-xs text-muted-foreground block mb-1">&larr; Previous</span>
                    <span className="text-sm text-white">
                      {prevDive.metadata?.customName || `Dive #${prevDive.diveNumber}`}
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">No previous dive</span>
                )}
              </button>
              <button
                onClick={goToNext}
                disabled={!nextDive}
                className="flex-1 text-right p-3 rounded-xl border border-white/10 bg-[#081a32]/85 backdrop-blur-sm shadow-lg cursor-pointer hover:bg-[#0a2040] hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                aria-label="Next dive"
              >
                {nextDive ? (
                  <>
                    <span className="text-xs text-muted-foreground block mb-1">Next &rarr;</span>
                    <span className="text-sm text-white">
                      {nextDive.metadata?.customName || `Dive #${nextDive.diveNumber}`}
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">No next dive</span>
                )}
              </button>
            </div>
          </main>
        )}
      </div>

      {/* Bubbles overlay - renders in front of content */}
      <Bubbles
        bubbleCount={50}
        speed={0.7}
        minSize={2}
        maxSize={6}
        opacity={1}
      />

      {/* Photo Lightbox */}
      <PhotoLightbox
        photo={selectedPhoto}
        media={selectedDive?.metadata?.media || []}
        onClose={() => setSelectedPhoto(null)}
        onNavigate={setSelectedPhoto}
      />
    </div>
  );
}

export default App;
